import React, { useEffect, useState } from 'react';
import { Content, ContentType } from '../../types/content';
import useContentApi from '../../api/useContentApi';
import { 
  Box, 
  Button, 
  Container, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Typography, 
  CircularProgress, 
  Alert, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle
} from '@mui/material';
import ContentFormModal from './ContentFormModal';

const ContentManagement: React.FC = () => {
  const [contents, setContents] = useState<Content[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState<boolean>(false);
  const [editingContent, setEditingContent] = useState<Content | null>(null);
  
  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [contentToDelete, setContentToDelete] = useState<string | null>(null);

  const { getContents, updateContent, createContent, deleteContent } = useContentApi();

  useEffect(() => {
    fetchContents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchContents = async () => {
    try {
      setLoading(true);
      const data = await getContents();
      setContents(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch contents:', err);
      setError('נכשל בטעינת התוכן');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (content: Content) => {
    setEditingContent(content);
    setFormModalOpen(true);
  };

  const handleAddNew = () => {
    setEditingContent(null);
    setFormModalOpen(true);
  };

  const handleFormSubmit = async (formData: { key: string; title: string; content: string; type: ContentType }) => {
    try {
      if (editingContent) {
        // Update existing content
        await updateContent(editingContent.id, formData);
      } else {
        // Create new content
        await createContent(formData);
      }

      // Refresh list
      fetchContents();
    } catch (err) {
      console.error('Failed to save content:', err);
      throw err; // Let the modal handle the error
    }
  };

  const openDeleteDialog = (id: string) => {
    setContentToDelete(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setContentToDelete(null);
  };

  const handleDelete = async () => {
    if (contentToDelete) {
      try {
        await deleteContent(contentToDelete);
        fetchContents();
        closeDeleteDialog();
      } catch (err) {
        console.error('Failed to delete content:', err);
        setError('נכשל במחיקת התוכן');
        closeDeleteDialog();
      }
    }
  };

  if (loading && contents.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={3} sx={{ p: 3, position: 'relative', mb: 4 }}>
        <Typography variant="h5" component="h1" gutterBottom align="right">
          ניהול תוכן
        </Typography>
        
        <TableContainer>
          <Table dir="rtl">
            <TableHead>
              <TableRow>
                <TableCell align="right">מפתח</TableCell>
                <TableCell align="right">סוג</TableCell>
                <TableCell align="right">כותרת</TableCell>
                <TableCell align="right">תוכן</TableCell>
                <TableCell align="right">פעולות</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    לא נמצא תוכן. הוסף תוכן כדי להתחיל.
                  </TableCell>
                </TableRow>
              ) : (
                contents.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell align="right">{item.key}</TableCell>
                    <TableCell align="right">{item.type}</TableCell>
                    <TableCell align="right">{item.title || '-'}</TableCell>
                    <TableCell dir="rtl" align="right">
                      {item.content.length > 50
                        ? `${item.content.substring(0, 50)}...`
                        : item.content}
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          color="primary" 
                          onClick={() => handleEdit(item)}
                        >
                          עריכה
                        </Button>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          color="error" 
                          onClick={() => openDeleteDialog(item.id)}
                        >
                          מחיקה
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Box display="flex" justifyContent="flex-start" mt={2} dir="rtl">
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleAddNew}
          >
            הוספת תוכן חדש
          </Button>
        </Box>
      </Paper>

      {/* Content Form Modal */}
      <ContentFormModal
        open={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        onSubmit={handleFormSubmit}
        initialData={editingContent}
        title={editingContent ? 'עריכת תוכן' : 'הוספת תוכן חדש'}
      />

      {/* Confirmation Dialog for Delete */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        dir="rtl"
        PaperProps={{
          sx: {
            maxWidth: '500px',
            width: '100%'
          }
        }}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">אישור מחיקה</DialogTitle>
        <DialogContent dividers>
          <DialogContentText id="delete-dialog-description" dir="rtl">
            האם אתה בטוח שברצונך למחוק תוכן זה? פעולה זו אינה ניתנת לביטול.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-start', direction: 'rtl', p: 2 }}>
          <Button onClick={handleDelete} color="error" variant="contained">
            מחיקה
          </Button>
          <Button onClick={closeDeleteDialog} color="primary">
            ביטול
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ContentManagement;